import { useEffect, useState } from 'react';
import { getSupabase, isSupabaseConfigured } from './supabaseClient';
import {
  LAYOUT_PREVIEW_SESSION_ID,
  useDevMaxiNoSession,
  useLayoutPreview,
} from './layoutPreviewContext';
import { getVoteSessionIdFromCurrentUrl } from './publicVoteUrl';

const STORAGE_KEY = 'ipb_active_session_id';
const CHANGE_EVENT = 'ipb:active-session-change';

export function getActiveSessionId(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function setActiveSessionId(id: string) {
  localStorage.setItem(STORAGE_KEY, id);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: id }));
}

export function clearActiveSessionId() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: null }));
}

/**
 * Restituisce l'id della sessione evento attiva.
 *
 * Procedura:
 *   1. Se in localStorage c'è un id, ne verifica l'esistenza nel DB (status='open').
 *      Se la riga non esiste più (es. ricreata), lo scarta.
 *   2. Altrimenti (o se scartato) interroga le `event_sessions` con `status='open'`
 *      più recenti e prende la prima. Salva la scelta in localStorage.
 */
export function useActiveSessionId(): { sessionId: string | null; loading: boolean } {
  const noSess = useDevMaxiNoSession();
  const preview = useLayoutPreview();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    function onChange() {
      setBump((n) => n + 1);
    }
    window.addEventListener(CHANGE_EVENT, onChange as EventListener);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange as EventListener);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  useEffect(() => {
    if (noSess) {
      setSessionId(null);
      setLoading(false);
      return;
    }
    if (preview) {
      setSessionId(LAYOUT_PREVIEW_SESSION_ID);
      setLoading(false);
      return;
    }

    const voteSessionFromUrl = getVoteSessionIdFromCurrentUrl();
    if (voteSessionFromUrl) {
      if (getActiveSessionId() !== voteSessionFromUrl) setActiveSessionId(voteSessionFromUrl);
      setSessionId(voteSessionFromUrl);
      setLoading(false);
      return;
    }

    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }
    const supabase = getSupabase();
    let cancelled = false;
    setLoading(true);

    async function resolve() {
      const stored = getActiveSessionId();
      if (stored) {
        const { data } = await supabase
          .from('event_sessions')
          .select('id')
          .eq('id', stored)
          .eq('status', 'open')
          .maybeSingle();
        if (cancelled) return;
        if (data?.id) {
          setSessionId(data.id);
          setLoading(false);
          return;
        }
        clearActiveSessionId();
      }

      const { data: latest } = await supabase
        .from('event_sessions')
        .select('id')
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const id = (latest as { id?: string } | null)?.id ?? null;
      if (id) {
        setActiveSessionId(id);
        setSessionId(id);
      } else {
        setSessionId(null);
      }
      setLoading(false);
    }

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [bump, noSess, preview]);

  return { sessionId, loading };
}
